"use client";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import {
  hotelSearchReducer,
  initialState,
} from "@/reducers/hotelSearchReducer";
import { hotelSearchSchema } from "@/schemas/hotelSearchSchema";
import { hotelServices } from "@/services/hotelServices";
import { staticDataService } from "@/services/staticService";
import { formatDate } from "@/utils/dateFormatter";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useReducer, useRef } from "react";

const DEBOUNCE_DELAY = 300; // ms

export const useHotelSearch = (initialData = {}) => {
  // Initial data ile state'i merge et
  const mergedInitialState = {
    ...initialState,
    ...initialData,
  };

  // Guests için özel merge - initialData.guests varsa default değerlerle birleştir
  if (initialData.guests) {
    mergedInitialState.guests = {
      ...initialState.guests,
      ...initialData.guests,
    };
  }

  const [state, dispatch] = useReducer(hotelSearchReducer, mergedInitialState);
  const locale = useLocale();
  const t = useTranslationDetector("hotelSearch");
  const autocompleteTimeoutRef = useRef(null);

  const setLoading = (loading) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  };

  const setCheckinDate = (checkinDate) => {
    dispatch({ type: "SET_CHECKIN_DATE", payload: checkinDate });
  };

  const setCheckoutDate = (checkoutDate) => {
    dispatch({ type: "SET_CHECKOUT_DATE", payload: checkoutDate });
  };

  const setLocation = useCallback((location) => {
    dispatch({ type: "SET_LOCATION", payload: location });
  }, []);

  const setGuests = (data) => {
    dispatch({ type: "SET_GUESTS", payload: data });
  };

  const setLatestHotelSearches = (latestHotelSearches) => {
    dispatch({
      type: "SET_LATEST_HOTEL_SEARCHES",
      payload: latestHotelSearches,
    });
  };

  const setPopularLocations = (popularCities) => {
    dispatch({ type: "SET_POPULAR_LOCATIONS", payload: popularCities });
  };

  const setPublicHolidays = (publicHolidays) => {
    dispatch({ type: "SET_PUBLIC_HOLIDAYS", payload: publicHolidays });
  };

  // Bus için sadece gerekli fonksiyonları kullan
  const getPopularLocations = async () => {
    try {
      const popularCitiesResponse =
        await hotelServices.search.getPopularLocations({
          LanguageCode: locale.toUpperCase(),
        });
      const popularCities = (popularCitiesResponse?.PopularSearches || [])
        .slice(0, 10)
        .map((item) => ({
          label: item?.Name || "",
          AutoCompleteResult: {
            label: item?.Name || "",
            value: item?.Name || "",
            id: item?.Slug || "",
          },
        }));
      setPopularLocations(popularCities);
    } catch (error) {
      console.error(
        "getPopularCities: Popular cities yüklenirken hata oluştu:",
        error,
      );
    }
  };

  // Lazy-load flags and loaders
  const ensurePopularLoaded = useCallback(async () => {
    try {
      // Load if missing only
      if (!state.popularLocations) {
        await getPopularLocations();
      }
    } catch (_) {
      // no-op, errors already logged inside fetchers
    }
  }, [state.popularLocations]);

  // Search for hotel locations using hotel autocomplete service
  const searchForHotelLocations = useCallback(
    (query) => {
      if (!query || query.length < 3) {
        return Promise.resolve(null);
      }

      if (autocompleteTimeoutRef.current) {
        clearTimeout(autocompleteTimeoutRef.current);
      }

      return new Promise((resolve, reject) => {
        autocompleteTimeoutRef.current = setTimeout(async () => {
          try {
            const response = await hotelServices.search.autocomplete(
              query,
              locale,
            );
            resolve(response);
          } catch (err) {
            reject(err);
          }
        }, DEBOUNCE_DELAY);
      });
    },
    [locale, hotelServices],
  );

  const normalizeText = useCallback((str) => {
    if (!str || typeof str !== "string") return "";
    return str.trim().toLocaleLowerCase("tr");
  }, []);

  // Şehir kodundan şehir bilgisi getiren fonksiyon
  const getCityInfoByCode = useCallback(
    async (cityCode) => {
      try {
        const languageCode = (locale || "tr").split("-")[0].toLowerCase();
        const slugResponse = await hotelServices.basic.getContentBySlug({
          Slug: cityCode,
          LanguageCode: languageCode,
        });
        const locationName = slugResponse?.Name;
        if (!locationName) {
          return null;
        }

        const response = await hotelServices.search.autocomplete(
          locationName,
          locale,
        );
        if (!response || response.length === 0) return null;

        // API camelCase veya PascalCase dönebilir; her iki formu da oku
        const getField = (result, key) =>
          result[key] ?? result[key.charAt(0).toUpperCase() + key.slice(1)];
        const normalizedLocationName = normalizeText(locationName);
        const exactMatch = response.find((result) => {
          const value = normalizeText(getField(result, "value"));
          const label = normalizeText(getField(result, "label"));
          return (
            value === normalizedLocationName || label === normalizedLocationName
          );
        });

        return exactMatch || null;
      } catch (error) {
        console.error(
          `getCityInfoByCode: ${cityCode} için şehir bilgisi getirilemedi:`,
          error,
        );
        return null;
      }
    },
    [locale, normalizeText],
  );

  // City selection handlers - bus için basitleştirilmiş
  const onSelectLocation = useCallback(
    (location) => {
      setLocation(location);
    },
    [setLocation],
  );

  // Utility functions
  const formatDateForUrl = useCallback((date) => {
    return formatDate(date, { format: "YYYY-MM-DD" });
  }, []);

  const generateQuery = (searchData) => {
    // Location kontrolü - eğer location yoksa URL oluşturma
    if (!searchData.location || !searchData.location.id) {
      return null;
    }

    // Flight search ile aynı formatta tek parametre olarak birleştir
    const stationInfo = searchData.location.id;
    const checkinDate = formatDateForUrl(
      searchData.checkinDateRaw || searchData.checkinDate,
    );
    const checkoutDate = formatDateForUrl(
      searchData.checkoutDateRaw || searchData.checkoutDate,
    );

    // Query parametreleri oluştur
    const queryParams = new URLSearchParams();

    // Hotel yolcu bilgilerini ekle (sadece 0'dan büyük olanları)
    // Hotel için: adult, young, child
    if (searchData.guests.adult > 0)
      queryParams.set("adult", searchData.guests.adult.toString());
    if (searchData.guests.child > 0) {
      queryParams.set("child", searchData.guests.child.toString());
      queryParams.set("childAges", searchData.guests.childrenAges.join(","));
    }

    const queryString = queryParams.toString();
    const baseUrl = `/hotel/search/${stationInfo}/${checkinDate}/${checkoutDate}`;

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  };

  // Hotel için URL oluşturma
  const generateHotelSearchUrl = useCallback(() => {
    return generateQuery(state);
  }, [
    state.location,
    state.checkinDate,
    state.checkoutDate,
    formatDateForUrl,
    state.guests,
  ]);

  // Validation
  const validateForm = useCallback(() => {
    const formData = {
      location: state.location,
    };

    const errors = {};

    // Location validation - önce açık kontrol
    if (!state.location || !state.location.id) {
      errors.location = t("locationRequired");
    }

    // Schema validation
    try {
      hotelSearchSchema.parse(formData);
    } catch (error) {
      error.errors.forEach((err) => {
        // Eğer location hatası varsa ve zaten errors.location yoksa ekle
        if (err.path[0] === "location" && !errors.location) {
          errors[err.path[0]] = err.message;
        }
      });
    }

    // Date validation - flight search mantığına benzer
    if (!state.checkinDate) {
      errors.checkinDate = t("checkinDateRequired");
    }
    if (!state.checkoutDate) {
      errors.checkoutDate = t("checkoutDateRequired");
    }
    if (
      state.guests.child > 0 &&
      state.guests.childrenAges.length !== state.guests.child
    ) {
      errors.childrenAges = t("childrenAgesRequired");
    }

    return errors;
  }, [state.location, state.checkinDate, state.checkoutDate, state.guests, t]);

  const getPublicHolidays = async () => {
    try {
      const publicHolidaysResponse = await staticDataService.getStaticData({
        LanguageCode: locale.toUpperCase(),
        PageName: "PublicHolidays",
        GroupNames: [
          new Date().getMonth() === 11
            ? new Date().getFullYear() + 1
            : new Date().getFullYear(),
        ],
      });
      const publicHolidays = JSON.parse(
        publicHolidaysResponse?.ContentParts[0]?.ContentGroupKeys[0]
          ?.ContentGroupValues[0]?.Value,
      ).map((item) => ({
        date: item.date,
        title: item.localName,
      }));
      setPublicHolidays(publicHolidays);
    } catch (error) {
      console.error(
        "getPublicHolidays: Public holidays yüklenirken hata oluştu:",
        error,
      );
    }
  };

  const getHotelDefaultOrigin = async () => {
    try {
      const defaultCityOrigin = t("defaultCityOrigin");
      const response = await hotelServices.search.autocomplete(
        defaultCityOrigin,
        locale,
      );
      setLocation(response?.[0] || null);
    } catch (error) {
      console.error(
        "getBusDefaultOrigin: Bus default origin yüklenirken hata oluştu:",
        error,
      );
    }
  };

  const getLocationCityText = (location) => {
    const parts = [];

    if (location?.district) {
      parts.push(location.district);
    }

    if (location?.city) {
      parts.push(location.city);
    }

    return parts.join(", ");
  };

  const getGuestInfoText = (guestList) => {
    const parts = [];

    if (guestList?.adult > 0) {
      const guestText = t ? t("guestAdult") : "Yetişkin";
      parts.push(`${guestList.adult} ${guestText}`);
    }

    if (guestList?.child > 0) {
      const childText = t ? t("guestChild") : "Çocuk";
      parts.push(`${guestList.child} ${childText}`);
    }

    return parts.join(", ");
  };

  const getLatestHotelSearches = () => {
    const STORAGE_KEY = initialData.storageKey || "latestHotelSearches";
    const latestHotelSearches = localStorage.getItem(STORAGE_KEY);
    if (latestHotelSearches) {
      const parsedSearches = JSON.parse(latestHotelSearches);
      if (!Array.isArray(parsedSearches)) {
        setLatestHotelSearches([]);
        return;
      }
      // Geçmiş tarihli aramalar silinmez, listede kalır; tıklanınca tarihler güncellenir
      const limitedSearches =
        parsedSearches.length > 3 ? parsedSearches.slice(0, 3) : parsedSearches;
      setLatestHotelSearches(limitedSearches);
    }
  };

  useEffect(() => {
    getLatestHotelSearches();

    // Location varsayılan olarak boş olmalı - kullanıcı kendisi seçecek
    // getHotelDefaultOrigin() çağrısı kaldırıldı
  }, [locale]);

  // Initial data'da şehir kodları varsa API'den gerçek şehir bilgilerini getir
  useEffect(() => {
    const loadInitialLocationData = async () => {
      try {
        // Departure city kodunu kontrol et
        if (initialData.initialLocationCode && !state.location) {
          const locationData = await getCityInfoByCode(
            initialData.initialLocationCode,
          );
          if (locationData) {
            setLocation(locationData);
          }
        }
      } catch (error) {
        console.error(
          "loadInitialLocationData: Initial şehir bilgileri yüklenirken hata oluştu:",
          error,
        );
      }
    };

    // Sadece locale yüklendiyse çalıştır
    if (locale && initialData.initialLocationCode) {
      loadInitialLocationData();
    }
  }, [locale, initialData.initialLocationCode]);

  return {
    // Hotel-specific state mapping
    location: state.location,
    checkinDate: state.checkinDate,
    checkoutDate: state.checkoutDate,
    guests: state.guests,
    latestHotelSearches: state.latestHotelSearches,
    popularLocations: state.popularLocations,
    publicHolidays: state.publicHolidays,

    // Actions
    setCheckinDate,
    setCheckoutDate,
    setLocation,
    setGuests,

    // Service functions
    searchForHotelLocations,
    onSelectLocation,
    ensurePopularLoaded,

    // Utility functions
    validateForm,
    generateHotelSearchUrl,
    refreshPublicHolidays: getPublicHolidays,
    getLocationCityText,
    getGuestInfoText,
    getLatestHotelSearches,
    generateQuery,

    // State for compatibility
    state,
  };
};
